import { collection, getDocs, query, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../database/firebase';

/**
 * Get a single user by ID
 * @param {string} userId - User document ID
 * @returns {Promise<Object|null>} User object or null
 */
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return {
        id: userSnap.id,
        ...userSnap.data(),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    throw error;
  }
};

/**
 * Fetch all users from Firestore
 * @returns {Promise<Array>} Array of user objects
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    // Remove orderBy to support both old and new user structures
    // Some users have createdAt at root, others have metadata.createdAt
    const querySnapshot = await getDocs(usersRef);

    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort in JavaScript to handle both structures
    users.sort((a, b) => {
      const dateA = new Date(a.metadata?.createdAt || a.createdAt || 0);
      const dateB = new Date(b.metadata?.createdAt || b.createdAt || 0);
      return dateB - dateA; // Descending order (newest first)
    });

    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Update user role
 * @param {string} userId - User document ID
 * @param {string} role - New role (user, admin, superadmin)
 * @returns {Promise<void>}
 */
export const updateUserRole = async (userId, role) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Update user status (active/inactive)
 * @param {string} userId - User document ID
 * @param {boolean} isActive - Active status
 * @returns {Promise<void>}
 */
export const updateUserStatus = async (userId, isActive) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

/**
 * Update user details (all fields)
 * @param {string} userId - User document ID
 * @param {Object} userData - User data to update
 * @returns {Promise<void>}
 */
export const updateUserDetails = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating user details:', error);
    throw error;
  }
};

/**
 * Delete user from both Firestore and Firebase Authentication.
 * Step 1: Delete Firestore document directly.
 * Step 2: Delete Firebase Auth account via the Cloud Function (httpsCallable).
 * Falls back gracefully if Auth deletion fails (Firestore doc is still removed).
 * @param {string} userId - User document ID / Firebase Auth UID
 * @returns {Promise<void>}
 */
export const deleteUser = async (userId) => {
  try {
    // Step 1: Delete the user document from Firestore
    const userRef = doc(db, 'users', userId);
    await deleteDoc(userRef);
    console.log(`Deleted Firestore document for user ${userId}`);

    // Step 2: Delete from Firebase Authentication via Cloud Function
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const deleteUserFn = httpsCallable(functions, 'deleteUser');
      await deleteUserFn({ userId });
      console.log(`Deleted Firebase Auth account for user ${userId}`);
    } catch (authErr) {
      // Auth deletion failed — log but don't block the success flow
      // (Firestore doc is already deleted which is the primary record)
      console.warn('Could not delete Firebase Auth account (may already be removed):', authErr.message);
    }

    return { success: true, userId };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw new Error(error.message || 'Failed to delete user');
  }
};


